import React from 'react';
import { AlertTriangle, Scale } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SEOHead, { ADVISOR_NAME, ADVISOR_PHONE_DISPLAY, ADVISOR_EMAIL } from '../components/SEOHead';

const Disclaimer = () => {
  const { i18n } = useTranslation();
  const isTe = i18n.language === 'te';

  return (
    <div className="min-h-screen pt-24 pb-20 bg-slate-50 dark:bg-slate-950">
      <SEOHead
        title={isTe ? 'చట్టబద్ధమైన బీమా నిరాకరణ | VSR Insurance Plus' : 'Statutory Insurance Disclaimer | VSR Insurance Plus'}
        description={isTe ? 'IRDAI నిబంధనలు, సెక్షన్ 41 రిబేట్ నిషేధం మరియు VSR Insurance Plus ఏజెన్సీ చట్టబద్ధమైన బహిర్గత ప్రకటన.' : 'Statutory insurance regulatory disclaimers, IRDAI compliance statements, Section 41 rebate prohibition, and agency disclosures for VSR Insurance Plus.'}
        canonicalPath="/disclaimer"
        breadcrumbs={[{ name: isTe ? 'నిరాకరణ' : 'Disclaimer', path: '/disclaimer' }]}
      />

      <div className="container mx-auto px-6 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 p-2 px-4 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            <AlertTriangle className="w-3.5 h-3.5" /> {isTe ? 'రెగ్యులేటరీ నిబంధనల నోటీసు' : 'Regulatory Compliance Notice'}
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-4 tracking-tight">
            {isTe ? 'బీమా రెగ్యులేటరీ నిరాకరణ ప్రకటన' : 'Insurance Regulatory Disclaimer'}
          </h1>
          <p className="text-sm text-slate-500">
            {isTe ? 'IRDAI మార్గదర్శకాలు మరియు భారతీయ చట్టాల ప్రకారం తప్పనిసరి ప్రకటనలు' : 'Mandatory disclosures in compliance with IRDAI guidelines & Indian Law'}
          </p>
        </div>

        <div className="bg-white dark:bg-slate-900 p-8 md:p-12 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-8 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
          <section className="bg-amber-50 dark:bg-amber-950/30 p-6 rounded-2xl border border-amber-200 dark:border-amber-900/50 text-amber-900 dark:text-amber-200">
            <h2 className="text-lg font-bold mb-2 flex items-center gap-2">
              <Scale className="w-5 h-5 text-amber-600 dark:text-amber-400" /> {isTe ? 'బీమా అభ్యర్థన విషయం' : 'Subject Matter of Solicitation'}
            </h2>
            <p className="font-medium text-xs leading-relaxed">
              {isTe
                ? '"బీమా అనేది అభ్యర్థన విషయం (INSURANCE IS THE SUBJECT MATTER OF THE SOLICITATION)." vsrinsuranceplus.com లో అందించబడిన సమాచారం విద్యాపరమైన అవగాహన మరియు పాలసీ పోలికల కొరకు మాత్రమే. బీమా పాలసీలను సంబంధిత బీమా సంస్థ జారీ చేసిన అధికారిక డాక్యుమెంట్లు మరియు నిబంధనల ఆధారంగా మాత్రమే తీసుకోవాలి.'
                : '"INSURANCE IS THE SUBJECT MATTER OF THE SOLICITATION." The information provided on vsrinsuranceplus.com is designed for educational awareness, policy comparison, and consumer guidance. Insurance policies must be purchased strictly on the basis of official sales literature, policy bonds, and terms issued by the respective insurer.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
              {isTe ? '1. ఏజెన్సీ మరియు కార్యాచరణ గుర్తింపు' : '1. Agency & Operational Identity'}
            </h2>
            <p>
              {isTe
                ? 'విఎస్ఆర్ ఇన్సూరెన్స్ ప్లస్ (ఎస్వీఆర్ ఎల్ఐసి సర్వీసెస్, హైదరాబాద్) అనేది సీనియర్ లైఫ్ ఇన్సూరెన్స్ సలహాదారు వి. ఎస్. రెడ్డి నేతృత్వంలోని స్వతంత్ర బీమా ఏజెన్సీ. మేము లైఫ్ ఇన్సూరెన్స్ కార్పొరేషన్ ఆఫ్ ఇండియా (ఎల్ఐసి) మరియు హెచ్‌డిఎఫ్‌సి ఎర్గో జనరల్ ఇన్సూరెన్స్ కంపెనీల అధికారిక ఏజెన్సీ భాగస్వామి.'
                : 'VSR Insurance Plus (represented under SVR LIC Services, Hyderabad) is an independent insurance advisory agency led by Senior Life Insurance Advisor V. S. Reddy. We are an authorized distribution partner and agency for Life Insurance Corporation of India (LIC) and HDFC ERGO General Insurance Company Ltd.'}
            </p>
            <p className="mt-2">
              {isTe ? (
                <>విఎస్ఆర్ ఇన్సూరెన్స్ ప్లస్ అనేది ఎల్ఐసి ప్రధాన కార్పొరేట్ వెబ్‌సైట్ (<a href="https://licindia.in" target="_blank" rel="noreferrer" className="text-primary underline">licindia.in</a>) లేదా హెచ్‌డిఎఫ్‌సి ఎర్గో అధికారిక పోర్టల్ (<a href="https://www.hdfcergo.com" target="_blank" rel="noreferrer" className="text-primary underline">hdfcergo.com</a>) కాదు.</>
              ) : (
                <>VSR Insurance Plus is <strong>not the corporate head office</strong> or official website of Life Insurance Corporation of India (which is <a href="https://licindia.in" target="_blank" rel="noreferrer" className="text-primary underline">licindia.in</a>) or HDFC ERGO General Insurance Company (<a href="https://www.hdfcergo.com" target="_blank" rel="noreferrer" className="text-primary underline">hdfcergo.com</a>).</>
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
              {isTe ? '2. ఇన్సూరెన్స్ చట్టం 1938 లోని సెక్షన్ 41 (రిబేట్‌ల నిషేధం)' : '2. Section 41 of the Insurance Act, 1938 (Prohibition of Rebates)'}
            </h2>
            <div className="bg-slate-50 dark:bg-slate-950 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 italic text-xs leading-relaxed">
              <p className="mb-2">
                "(1) No person shall allow or offer to allow, either directly or indirectly, as an inducement to any person to take out or renew or continue an insurance in respect of any kind of risk relating to lives or property in India, any rebate of the whole or part of the commission payable or any rebate of the premium shown on the policy, nor shall any person taking out or renewing or continuing a policy accept any rebate, except such rebate as may be allowed in accordance with the published prospectuses or tables of the insurer."
              </p>
              <p>
                "(2) Any person making default in complying with the provisions of this section shall be liable for a penalty which may extend to ten lakh rupees."
              </p>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              {isTe
                ? '*చట్టం ప్రకారం, విఎస్ఆర్ ఇన్సూరెన్స్ ప్లస్ జీరో-రిబేట్ నైతిక విలువలను ఖచ్చితంగా పాటిస్తుంది. ఎటువంటి పరిస్థితుల్లోనూ కమిషన్ రిబేట్లు లేదా క్యాష్‌బ్యాక్‌లు ఇవ్వబడవు.'
                : '*In accordance with law, VSR Insurance Plus strictly adheres to zero-rebate ethical advisory. We do not offer cash-backs or commission rebates under any circumstance.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
              {isTe ? '3. పన్ను రాయితీల నిరాకరణ (సెక్షన్ 80సి, 80డి & 10(10డి))' : '3. Tax Disclaimers (Section 80C, 80D & 10(10D))'}
            </h2>
            <p>
              {isTe
                ? 'ఈ వెబ్‌సైట్‌లో పేర్కొన్న పన్ను ప్రయోజనాలు (లైఫ్ ఇన్సూరెన్స్ సెక్షన్ 80సి, హెల్త్ ఇన్సూరెన్స్ సెక్షన్ 80డి, మరియు మెచ్యూరిటీ సెక్షన్ 10(10డి)) భారత ఆదాయపు పన్ను చట్టం 1961 లోని తాజా నిబంధనల ప్రకారం పేర్కొనబడ్డాయి. పన్ను చట్టాల మార్పులకు అనుగుణంగా వ్యక్తిగత వివరాల కోసం మీ చార్టర్డ్ అకౌంటెంట్‌ను సంప్రదించాలి.'
                : 'Tax benefits mentioned across this website (including deductions under Section 80C for life insurance, Section 80D for health insurance, and tax exemptions on maturity under Section 10(10D)) are based on prevailing provisions of the Indian Income Tax Act, 1961 and amendments introduced under recent Finance Acts. Tax laws are subject to legislative amendment. Policyholders should consult their certified chartered accountant or tax advisor for individual tax evaluations.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
              {isTe ? '4. రిటర్న్స్ మరియు బోనస్ వివరాలు' : '4. Returns & Bonus Declarations'}
            </h2>
            <p>
              {isTe
                ? 'పార్టిసిపేటింగ్ ప్లాన్లలో ఎల్ఐసి గతంలో ప్రకటించిన బోనస్‌లు భవిష్యత్ బోనస్‌లకు ఖచ్చితమైన హామీ కావు. నాన్-పార్టిసిపేటింగ్ ప్లాన్లలో (జీవన్ ఉత్సవ్ వంటివి) రాబడి పాలసీ బాండ్‌లో ఒప్పందపరంగా హామీ ఇవ్వబడుతుంది.'
                : 'Under participating life insurance policies (with-profits plans), past bonus declarations by LIC are not a contractual guarantee of future bonus rates. Final maturity values depend on the actual bonuses declared by LIC each financial year. For non-participating plans (such as LIC Jeevan Utsav or Bima Jyoti), returns are contractually guaranteed by the insurer in the policy contract.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
              {isTe ? '5. సలహాదారు సంప్రదింపు వివరాలు' : '5. Advisor Contact & Questions'}
            </h2>
            <p>
              {isTe ? 'ఏవైనా ప్రశ్నలు లేదా వివరణల కోసం నేరుగా సంప్రదించండి:' : 'For any clarification regarding statutory disclosures or policy terms, please reach out directly:'}
            </p>
            <div className="p-4 bg-slate-50 dark:bg-slate-950 rounded-xl space-y-1 text-xs mt-2">
              <p><strong>{isTe ? 'సలహాదారు:' : 'Advisor:'}</strong> {ADVISOR_NAME}</p>
              <p><strong>{isTe ? 'ఏజెన్సీ:' : 'Agency:'}</strong> VSR Insurance Plus (SVR LIC Services)</p>
              <p><strong>{isTe ? 'ప్రత్యక్ష హెల్ప్‌లైన్:' : 'Direct Helpline:'}</strong> {ADVISOR_PHONE_DISPLAY}</p>
              <p><strong>{isTe ? 'ఇమెయిల్:' : 'Email:'}</strong> {ADVISOR_EMAIL}</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Disclaimer;
